import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@components/ui/combobox';
import type { Comparator } from '@domain/types/filters';
import clsx from 'clsx';

import { APP_FOCUS_RING_OVERRIDE } from '../constants';
import type { FilterRow } from '../types';
import type { FlagMode, FlagSelection } from '../utils/flag-field-utils';
import { filterToFlagSelection, FLAG_OPTIONS, flagSelectionToFilter } from '../utils/flag-field-utils';

type FlagOption = { label: string; value: FlagSelection };

interface FlagSelectProps {
  comparator: Comparator | '';
  values: string[];
  mode?: FlagMode;
  options?: FlagOption[];
  onUpdate: (patch: Partial<FilterRow>) => void;
}

export function FlagSelect({ comparator, values, mode = 'count', options = FLAG_OPTIONS, onUpdate }: FlagSelectProps) {
  const currentSelection = filterToFlagSelection(comparator, values);
  const selectedItem: FlagOption | null = currentSelection
    ? (options.find((o) => o.value === currentSelection) ?? null)
    : null;

  return (
    <Combobox
      items={options}
      value={selectedItem}
      onValueChange={(item) => {
        if (item === null) {
          onUpdate({ comparator: 'none', values: [] });
        }
        if (item) {
          const { comparator: c, values: v } = flagSelectionToFilter(item.value, mode);
          onUpdate({ comparator: c, values: v });
        }
      }}
    >
      <ComboboxInput
        readOnly
        className={clsx(
          'border-info-300 h-10 w-full overflow-hidden shadow-none dark:bg-white',
          APP_FOCUS_RING_OVERRIDE,
        )}
        placeholder="Select…"
        showClear={true}
      />
      <ComboboxContent>
        <ComboboxList>
          <ComboboxCollection>
            {(item: FlagOption) => (
              <ComboboxItem key={item.value} value={item}>
                {item.label}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
